import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DashboardDimension } from '../api';

const DESIRED_COLOUR = '#3b82f6'; // blue
const CURRENT_COLOUR = '#10b981'; // green

// DimensionChart renders one dashboard dimension (Wealth / Asset / Geo /
// Sector / Risk) as side-by-side Desired vs Current bars per bucket. The
// chart auto-sizes vertically based on bucket count so Sector (~10 rows)
// doesn't squash Wealth (2 rows).
export function DimensionChart({
  title,
  subtitle,
  dimension,
  bucketLabel,
  sortMode = 'currentDesc',
}: {
  title: string;
  subtitle?: string;
  dimension: DashboardDimension;
  bucketLabel?: string;
  sortMode?: 'currentDesc' | 'numericAsc';
}) {
  const buckets = sortMode === 'numericAsc' ? sortNumeric(dimension.buckets) : dimension.buckets;
  const data = buckets.map((b) => ({
    name: b.label,
    Desired: b.desired_pct,
    Current: b.current_pct,
  }));
  const rowCount = Math.max(data.length, 1);
  const height = Math.max(180, rowCount * 36 + 60);
  const showCoverage = dimension.coverage_pct > 0 && dimension.coverage_pct < 100;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{title}</h3>
        {subtitle && <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{subtitle}</span>}
      </div>
      {showCoverage && (
        <div style={{ margin: '0.25rem 0 0' }}>
          <p style={{ color: '#a16207', margin: 0, fontSize: '0.8rem' }}>
            Allocation data covers {dimension.coverage_pct.toFixed(0)}% of portfolio value — the
            rest shows as "Unknown".
          </p>
          {dimension.unknown_funds && dimension.unknown_funds.length > 0 && (
            <details style={{ marginTop: '0.2rem', fontSize: '0.8rem', color: '#666' }}>
              <summary style={{ cursor: 'pointer' }}>
                {dimension.unknown_funds.length === 1
                  ? '1 fund in "Unknown"'
                  : `${dimension.unknown_funds.length} funds in "Unknown"`}
              </summary>
              <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.2rem' }}>
                {dimension.unknown_funds.map((f) => (
                  <li key={f.fund_id}>
                    {f.fund_name} <span style={{ color: '#888' }}>· {fmtGBP(f.value_pence)}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {data.length === 0 ? (
        <p style={{ color: '#888', margin: '0.5rem 0 0' }}>
          Nothing to show yet — add holdings or set targets to populate this chart.
        </p>
      ) : (
        <div style={{ width: '100%', height, marginTop: '0.5rem' }}>
          <ResponsiveContainer>
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 4, right: 24, bottom: 4, left: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis
                type="number"
                domain={[0, 'dataMax']}
                tick={{ fontSize: 11, fill: '#666' }}
                tickFormatter={(v: number) => `${v}%`}
              />
              <YAxis
                dataKey="name"
                type="category"
                tick={{ fontSize: 12, fill: '#333' }}
                width={120}
                label={
                  bucketLabel
                    ? {
                        value: bucketLabel,
                        angle: -90,
                        position: 'insideLeft',
                        style: { fontSize: 11, fill: '#666' },
                      }
                    : undefined
                }
              />
              <Tooltip
                formatter={(v) => `${Number(v).toFixed(2)}%`}
                contentStyle={{ fontSize: '0.85rem' }}
              />
              <Legend wrapperStyle={{ fontSize: '0.85rem' }} />
              <Bar dataKey="Desired" fill={DESIRED_COLOUR} isAnimationActive={false}>
                {data.map((_, i) => (
                  <Cell key={`d-${i}`} />
                ))}
              </Bar>
              <Bar dataKey="Current" fill={CURRENT_COLOUR} isAnimationActive={false}>
                {data.map((_, i) => (
                  <Cell key={`c-${i}`} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// sortNumeric is the special-case sort for the Risk dimension where
// "1".."10" buckets should appear in numeric order (with "Unknown" last)
// rather than current-pct-desc.
const gbp = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0,
});

function fmtGBP(pence: number): string {
  return gbp.format(pence / 100);
}

function sortNumeric(buckets: DashboardDimension['buckets']) {
  const copy = [...buckets];
  copy.sort((a, b) => {
    const an = parseInt(a.label, 10);
    const bn = parseInt(b.label, 10);
    const aIsNum = !Number.isNaN(an);
    const bIsNum = !Number.isNaN(bn);
    if (aIsNum && bIsNum) return an - bn;
    if (aIsNum) return -1;
    if (bIsNum) return 1;
    return a.label.localeCompare(b.label);
  });
  return copy;
}
